import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X, Smartphone } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "ksb_install_dismissed_at";
const SIGNUP_FLAG = "ksb_new_signup";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Detect whether the app is already installed / running standalone.
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith("android-app://")
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function recentlyDismissed() {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at && Date.now() - at < DISMISS_COOLDOWN_MS;
}

/**
 * Install-to-home-screen nudge.
 * - Listens for the browser's `beforeinstallprompt` event on install-capable
 *   browsers (Chrome / Edge / Samsung / Android).
 * - Falls back to a manual "Add to Home Screen" instruction on iOS Safari.
 * - Auto-hides when the app is already installed (standalone display-mode),
 *   or when the user dismissed the nudge in the last 7 days.
 * - Shows immediately after signup (Signup flow sets `ksb_new_signup`), and
 *   otherwise stays hidden until the browser signals installability.
 */
export default function InstallNudge() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installedJustNow, setInstalledJustNow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // Already installed — never nudge.

    const justSignedUp = localStorage.getItem(SIGNUP_FLAG) === "1";
    const cooledDown = recentlyDismissed();

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      // If we're coming right out of signup OR the user hasn't dismissed
      // recently, surface the nudge.
      if (justSignedUp || !cooledDown) setVisible(true);
    };

    const onInstalled = () => {
      setInstalledJustNow(true);
      setVisible(false);
      localStorage.removeItem(SIGNUP_FLAG);
      // Clear dismiss so the confirmation toast can flash once.
      setTimeout(() => setInstalledJustNow(false), 4000);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires `beforeinstallprompt`. If the user just signed
    // up on iOS and isn't already installed, show the manual-instructions
    // sheet after a short delay.
    if (isIOS() && justSignedUp && !cooledDown) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    localStorage.removeItem(SIGNUP_FLAG);
    if (outcome !== "accepted") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    localStorage.removeItem(SIGNUP_FLAG);
  };

  if (installedJustNow) {
    return (
      <div
        data-testid="install-installed-toast"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#1E3A8A] text-white rounded-full px-5 py-2.5 shadow-xl flex items-center gap-2 text-sm animate-fade-up"
      >
        <Smartphone className="w-4 h-4" />
        KSB installed. Look for the icon on your home screen.
      </div>
    );
  }

  if (!visible) return null;
  const ios = isIOS();

  return (
    <div
      data-testid="install-nudge"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px] z-[60] rounded-2xl shadow-2xl border border-[#E4E7F5] dark:border-[#2D3559] bg-[#F8F9FE] dark:bg-[#131A3A] overflow-hidden animate-fade-up"
    >
      <div className="relative p-5">
        <button
          data-testid="install-dismiss-btn"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[#EEF0FA] dark:hover:bg-[#1A2049] flex items-center justify-center text-[#5B6478] dark:text-[#C4CBE8] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-8">
          <div className="w-11 h-11 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white font-display font-extrabold text-sm shrink-0 shadow-md">
            KSB
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-base text-[#0F1E4B] dark:text-[#E7EAF6]">
              Install KSB on your phone
            </div>
            <div className="text-xs text-[#5B6478] dark:text-[#C4CBE8] mt-1 leading-relaxed">
              Get one-tap access, faster load and offline-ready screens — no app store needed.
            </div>
          </div>
        </div>

        {ios ? (
          <div className="mt-4 text-xs text-[#5B6478] dark:text-[#C4CBE8] space-y-2 bg-[#EEF0FA] dark:bg-[#1A2049] rounded-lg p-3">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-white dark:bg-[#232A55] flex items-center justify-center">
                <Share className="w-3.5 h-3.5 text-[#1E3A8A] dark:text-[#A78BFA]" />
              </span>
              <span>1. Tap the <strong>Share</strong> icon in Safari</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-white dark:bg-[#232A55] flex items-center justify-center">
                <Plus className="w-3.5 h-3.5 text-[#1E3A8A] dark:text-[#A78BFA]" />
              </span>
              <span>2. Choose <strong>Add to Home Screen</strong></span>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex gap-2">
            <Button
              data-testid="install-later-btn"
              variant="outline"
              onClick={dismiss}
              className="flex-1 border-[#E4E7F5] dark:border-[#2D3559]"
            >
              Not now
            </Button>
            <Button
              data-testid="install-now-btn"
              onClick={install}
              disabled={!deferred}
              className="flex-1 bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white gap-2"
            >
              <Download className="w-4 h-4" /> Install
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
